import React from 'react'

/** Viewport height shrink that suggests the virtual keyboard is open. */
export const KEYBOARD_VIEWPORT_HEIGHT_DELTA_PX = 150

const NON_KEYBOARD_INPUT_TYPES = new Set([
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'file',
  'hidden',
  'image',
])

export const isEditableFormField = (element: Element | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) return false
  if (element.isContentEditable) return true
  const tag = element.tagName
  if (tag === 'TEXTAREA') return true
  if (tag === 'SELECT') return true
  if (tag !== 'INPUT') return false
  return !NON_KEYBOARD_INPUT_TYPES.has((element as HTMLInputElement).type.toLowerCase())
}

export const isKeyboardOpenFromViewport = (): boolean => {
  const vv = window.visualViewport
  if (!vv) return false
  return window.innerHeight - vv.height > KEYBOARD_VIEWPORT_HEIGHT_DELTA_PX
}

/** Detects virtual keyboard open state; falls back to focused form fields when visualViewport is unavailable. */
export const useVirtualKeyboardOpen = (enabled: boolean): boolean => {
  const [keyboardOpen, setKeyboardOpen] = React.useState(false)

  React.useEffect(() => {
    if (!enabled) {
      setKeyboardOpen(false)
      return
    }

    const vv = window.visualViewport

    if (vv) {
      const syncFromViewport = () => {
        setKeyboardOpen(isKeyboardOpenFromViewport())
      }
      syncFromViewport()
      vv.addEventListener('resize', syncFromViewport)
      vv.addEventListener('scroll', syncFromViewport)
      window.addEventListener('resize', syncFromViewport)
      return () => {
        vv.removeEventListener('resize', syncFromViewport)
        vv.removeEventListener('scroll', syncFromViewport)
        window.removeEventListener('resize', syncFromViewport)
      }
    }

    const syncFromFocus = () => {
      setKeyboardOpen(isEditableFormField(document.activeElement))
    }
    syncFromFocus()
    document.addEventListener('focusin', syncFromFocus)
    document.addEventListener('focusout', syncFromFocus)
    return () => {
      document.removeEventListener('focusin', syncFromFocus)
      document.removeEventListener('focusout', syncFromFocus)
    }
  }, [enabled])

  return keyboardOpen
}
